import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import moment from 'moment/moment';

import { paymentTypeMap } from 'components/pages/patient-source/Patients/constants';
import { PMWorkbookContext } from 'components/pages/patient-source/PmWorkbook/PMWorkbookContext';
import {
  allDateOperators,
  allSingleSelectOperators,
  allStandardNumberOperatorsWithoutIsAnyOf,
  containsIsEmptyIsNotEmpty,
  containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect,
  containsStandardOperator,
  isEmptyIsNotEmpty,
  isIsEmptyIsNotEmptySingleSelect,
  isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
  patientIdHasDuplicateFilterOperators,
  taskOperators
} from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/CustomFilterOperators/CustomFilterOperators';
import { DefaultCell } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/DefaultCell';
import { ExpandableCell } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/ExpandableCell/ExpandableCell';
import { ExpandableLinkList } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/ExpandableLinkList/ExpandableLinkList';
import { PMWorkbookEditTextField } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/PMWorkbookEditTextField/PMWorkbookEditTextField';
import { PMWorkbookTableDatePicker } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/PMWorkbookTableDatePicker/PMWorkbookTableDatePicker';
import { tasksStatusesMap } from 'components/root/Container/Layout/Tasks/Task/taskConstants';
import { generateUrlByKey } from 'components/root/router';

import { convertPatientStatus } from '../../../finance/SiteFinance/SiteFinanceTable/SiteFinanceTable';
import { dateValueGetterWithoutLocalTimezone } from '../../Patients/Logs/LinkLogRecord/LinkLogRecordModal/ParticipantTable/ParticipantTable';

const PM_WORKBOOK_MIN_COLUMN_WIDTH = 75;

export const usePMWorkbookColumns = () => {
  const { columnOptions } = useContext(PMWorkbookContext);
  const columnOptionsRef = useRef(columnOptions);

  useEffect(() => {
    columnOptionsRef.current = columnOptions;
  }, [columnOptions]);

  return useMemo(
    () => ({
      columns: [
        {
          field: 'siteName',
          headerName: 'Site',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: allSingleSelectOperators
        },
        {
          field: 'subjectId',
          headerName: 'Subject ID',
          group: 'Subject Details',
          width: 120,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell({ row: { patientUniqueIdentifier, ssuPatientId, subjectId } }) {
            const content = subjectId || 'No Subject ID';
            return (
              <div className="overflow-ellipsis" title={content}>
                <Link
                  to={generateUrlByKey('Patients.Patient Profile.Patient Studies', {
                    patientId: patientUniqueIdentifier,
                    ssuPatientId: ssuPatientId
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {content}
                </Link>
              </div>
            );
          },
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'patientId',
          headerName: 'IntElligo ID',
          group: 'Subject Details',
          width: 145,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: [...containsStandardOperator, patientIdHasDuplicateFilterOperators]
        },
        {
          field: 'paymentCard',
          headerName: 'Payment Card',
          group: 'Subject Details',
          width: 160,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          valueGetter: (value, row) => convertPaymentCard(row.paymentCard),
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'bar83Id',
          headerName: '83Bar ID',
          group: 'Subject Details',
          width: 145,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'edcVisitDate',
          headerName: 'Visit Date',
          group: 'Subject Details',
          width: 120,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { edcVisitDate } }) => <DefaultDateRenderCell value={edcVisitDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'patientSource',
          headerName: 'Location (Referral Source)',
          group: 'Subject Details',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'icf',
          headerName: 'ICF',
          group: 'Subject Details',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          filterOperators: isEmptyIsNotEmpty,
          valueGetter: (value, { icf }) => icf?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { icf } }) => <ExpandableLinkList data={icf} />
        },
        {
          field: 'hipaa',
          headerName: 'HIPAA',
          group: 'Subject Details',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          filterOperators: isEmptyIsNotEmpty,
          valueGetter: (value, { hipaa }) => hipaa?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { hipaa } }) => <ExpandableLinkList data={hipaa} />
        },
        {
          field: 'patientRecordTransferStatus',
          headerName: 'Record Request Transfer Status',
          group: 'Subject Details',
          width: 280,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'patientRecordTransferDate',
          headerName: 'Record Transfer Date',
          group: 'Subject Details',
          width: 190,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { patientRecordTransferDate } }) => (
            <DefaultDateRenderCell value={patientRecordTransferDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'patientStatus',
          headerName: 'Status',
          group: 'Subject Details',
          width: 120,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          valueGetter: (value, row) => convertPatientStatus(row.patientStatus),
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'edcInitiatedDate',
          headerName: 'EDC Initiated Date',
          group: 'Subject Details',
          width: 190,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { edcInitiatedDate } }) => <DefaultDateRenderCell value={edcInitiatedDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'edcCompletedBy',
          headerName: 'EDC Completed By',
          group: 'Subject Details',
          width: 190,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'orderInitiated',
          headerName: 'Order Initiated',
          group: 'Phlebotomy',
          width: 185,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { orderInitiated }, field }) => (
            <DefaultDateRenderCell value={orderInitiated} field={field} />
          ),
          editable: true,
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          filterOperators: allDateOperators
        },
        {
          field: 'orderNumber',
          headerName: 'Order Number',
          group: 'Phlebotomy',
          width: 160,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'orderStatus',
          headerName: 'Order Status',
          group: 'Phlebotomy',
          width: 155,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'phlebotomyConfirmedDate',
          headerName: 'Phlebotomy Confirmed Date',
          group: 'Phlebotomy',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { phlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={phlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'bloodWorkCompleted',
          headerName: 'Blood Work Completed',
          group: 'Phlebotomy',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'labRequisitionReceived',
          headerName: 'Lab Requisition Received',
          group: 'Phlebotomy',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          filterOperators: isEmptyIsNotEmpty,
          valueGetter: (value, { labRequisitionReceived }) =>
            labRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { labRequisitionReceived } }) => <ExpandableLinkList data={labRequisitionReceived} />
        },
        {
          field: 'daysFromScheduledBDToColo',
          headerName: 'Number of Days from Scheduled BD to Scheduled Colo',
          group: 'Phlebotomy',
          width: 420,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'number',
          renderCell: DefaultCell,
          filterOperators: allStandardNumberOperatorsWithoutIsAnyOf,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'edcCompletionDate',
          headerName: 'EDC Completion Date',
          group: 'Phlebotomy',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { edcCompletionDate } }) => <DefaultDateRenderCell value={edcCompletionDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'bloodDrawStipend',
          headerName: 'Blood Draw Stipend',
          group: 'Phlebotomy',
          width: 200,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'redrawOrderInitiated',
          headerName: 'Redraw Order Initiated',
          group: 'Repeat Blood Draw',
          width: 215,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { redrawOrderInitiated } }) => <DefaultDateRenderCell value={redrawOrderInitiated} />,
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          editable: true,
          filterOperators: allDateOperators
        },
        {
          field: 'redrawOrderNumber',
          headerName: 'Redraw Order Number',
          group: 'Repeat Blood Draw',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'redrawOrderStatus',
          headerName: 'Redraw Order Status',
          group: 'Repeat Blood Draw',
          width: 200,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'redrawPhlebotomyConfirmedDate',
          headerName: 'Redraw Phlebotomy Confirmed Date',
          group: 'Repeat Blood Draw',
          width: 380,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { redrawPhlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={redrawPhlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'redrawBloodworkCompleted',
          headerName: 'Redraw Bloodwork Completed',
          group: 'Repeat Blood Draw',
          width: 265,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'redrawLabRequisitionReceived',
          headerName: 'Redraw Lab Requisition Received',
          group: 'Repeat Blood Draw',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { redrawLabRequisitionReceived }) =>
            redrawLabRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { redrawLabRequisitionReceived } }) => (
            <ExpandableLinkList data={redrawLabRequisitionReceived} />
          ),
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'repeatEdcCompletionDate',
          headerName: 'EDC Completion Date',
          group: 'Repeat Blood Draw',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { repeatEdcCompletionDate } }) => (
            <DefaultDateRenderCell value={repeatEdcCompletionDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'redrawStipend',
          headerName: 'Redraw Stipend',
          group: 'Repeat Blood Draw',
          width: 170,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'dateOfSurveyEncounter',
          headerName: 'Date of Survey Encounter',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { dateOfSurveyEncounter }, field }) => (
            <DefaultDateRenderCell value={dateOfSurveyEncounter} field={field} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'eproStipend',
          headerName: 'ePRO Stipend',
          width: 170,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'dateOfScheduledColonoscopy',
          headerName: 'Date of Scheduled Colonoscopy',
          width: 280,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          group: 'Colonoscopy Procedure',
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { dateOfScheduledColonoscopy }, field }) => (
            <DefaultDateRenderCell value={dateOfScheduledColonoscopy} field={field} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'actualDateOfColonoscopy',
          headerName: 'Actual Date of Colonoscopy',
          group: 'Colonoscopy Procedure',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { actualDateOfColonoscopy }, field }) => (
            <DefaultDateRenderCell value={actualDateOfColonoscopy} field={field} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'daysFromActualBDToColo',
          headerName: 'Number of Days from Actual BD to Actual Colo',
          group: 'Colonoscopy Procedure',
          width: 370,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          type: 'number',
          filterOperators: allStandardNumberOperatorsWithoutIsAnyOf,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'reasonForColonoscopy',
          headerName: 'Reason for Colonoscopy',
          group: 'Colonoscopy Procedure',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'coloReportReceived',
          headerName: 'Colo Report Received',
          group: 'Colonoscopy Procedure',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { coloReportReceived }) => coloReportReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { coloReportReceived } }) => <ExpandableLinkList data={coloReportReceived} />,
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'pathologyReported',
          headerName: 'Pathology Reported',
          group: 'Colonoscopy Procedure',
          width: 195,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'pathReportReceived',
          headerName: 'Path Report Received',
          group: 'Colonoscopy Procedure',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { pathReportReceived }) => pathReportReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { pathReportReceived } }) => <ExpandableLinkList data={pathReportReceived} />,
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'colonoscopyEdcCompletionDate',
          headerName: 'EDC Completion Date',
          group: 'Colonoscopy Procedure',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { colonoscopyEdcCompletionDate } }) => (
            <DefaultDateRenderCell value={colonoscopyEdcCompletionDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'colonoscopyEncounterSignOffStatus',
          headerName: 'Encounter Sign-Off Status',
          group: 'Colonoscopy Procedure',
          width: 270,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'colonoscopyStipend',
          headerName: 'Colonoscopy Stipend',
          group: 'Colonoscopy Procedure',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year1OrderInitiated',
          headerName: 'Order Initiated: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1OrderInitiated } }) => <DefaultDateRenderCell value={year1OrderInitiated} />,
          editable: true,
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year1OrderNumber',
          headerName: 'Order Number: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'year1OrderStatus',
          headerName: 'Order Status: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year1PhlebotomyConfirmedDate',
          headerName: 'Phlebotomy Confirmed Date: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 315,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1PhlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={year1PhlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year1BloodDrawWindow',
          headerName: 'Blood Draw Window: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterable: false
        },
        {
          field: 'year1BloodWorkCompleted',
          headerName: 'Blood Work Completed: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year1DateOfBloodDraw',
          headerName: 'Date of Blood Draw: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1DateOfBloodDraw } }) => <DefaultDateRenderCell value={year1DateOfBloodDraw} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year1LabRequisitionReceived',
          headerName: 'Lab Requisition Received: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 330,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          filterOperators: isEmptyIsNotEmpty,
          valueGetter: (value, { year1LabRequisitionReceived }) =>
            year1LabRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { year1LabRequisitionReceived } }) => (
            <ExpandableLinkList data={year1LabRequisitionReceived} />
          )
        },
        {
          field: 'year1EdcCompletionDate',
          headerName: 'EDC Completion Date: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1EdcCompletionDate } }) => <DefaultDateRenderCell value={year1EdcCompletionDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year1BloodDrawStipend',
          headerName: 'Blood Draw Stipend: Year 1',
          group: 'Year 1 Phlebotomy',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year1RedrawOrderInitiated',
          headerName: 'Redraw Order Initiated: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 285,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1RedrawOrderInitiated } }) => (
            <DefaultDateRenderCell value={year1RedrawOrderInitiated} />
          ),
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          editable: true,
          filterOperators: allDateOperators
        },
        {
          field: 'year1RedrawOrderNumber',
          headerName: 'Redraw Order Number: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 275,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'year1RedrawOrderStatus',
          headerName: 'Redraw Order Status: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 265,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year1RedrawPhlebotomyConfirmedDate',
          headerName: 'Redraw Phlebotomy Confirmed Date: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 390,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1RedrawPhlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={year1RedrawPhlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year1RedrawBloodworkCompleted',
          headerName: 'Redraw Bloodwork Completed: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 310,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year1DateOfBloodRedraw',
          headerName: 'Date of Blood Redraw: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 275,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1DateOfBloodRedraw } }) => <DefaultDateRenderCell value={year1DateOfBloodRedraw} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year1RedrawLabRequisitionReceived',
          headerName: 'Redraw Lab Requisition Received: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 380,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { year1RedrawLabRequisitionReceived }) =>
            year1RedrawLabRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { year1RedrawLabRequisitionReceived } }) => (
            <ExpandableLinkList data={year1RedrawLabRequisitionReceived} />
          ),
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'year1RedrawEdcCompletionDate',
          headerName: 'EDC Completion Date: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year1RedrawEdcCompletionDate } }) => (
            <DefaultDateRenderCell value={year1RedrawEdcCompletionDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year1RedrawStipend',
          headerName: 'Redraw Stipend: Year 1',
          group: 'Year 1 Repeat Blood Draw',
          width: 240,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year2OrderInitiated',
          headerName: 'Order Initiated: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2OrderInitiated } }) => <DefaultDateRenderCell value={year2OrderInitiated} />,
          editable: true,
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year2OrderNumber',
          headerName: 'Order Number: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'year2OrderStatus',
          headerName: 'Order Status: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year2PhlebotomyConfirmedDate',
          headerName: 'Phlebotomy Confirmed Date: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 320,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2PhlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={year2PhlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year2BloodDrawWindow',
          headerName: 'Blood Draw Window: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterable: false
        },
        {
          field: 'year2BloodWorkCompleted',
          headerName: 'Blood Work Completed: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field],
          filterOperators: isIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year2DateOfBloodDraw',
          headerName: 'Date of Blood Draw: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2DateOfBloodDraw } }) => <DefaultDateRenderCell value={year2DateOfBloodDraw} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year2LabRequisitionReceived',
          headerName: 'Lab Requisition Received: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 330,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          filterOperators: isEmptyIsNotEmpty,
          valueGetter: (value, { year2LabRequisitionReceived }) =>
            year2LabRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { year2LabRequisitionReceived } }) => (
            <ExpandableLinkList data={year2LabRequisitionReceived} />
          )
        },
        {
          field: 'year2EdcCompletionDate',
          headerName: 'EDC Completion Date: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2EdcCompletionDate } }) => <DefaultDateRenderCell value={year2EdcCompletionDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year2BloodDrawStipend',
          headerName: 'Blood Draw Stipend: Year 2',
          group: 'Year 2 Phlebotomy',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year2RedrawOrderInitiated',
          headerName: 'Redraw Order Initiated: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 285,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2RedrawOrderInitiated } }) => (
            <DefaultDateRenderCell value={year2RedrawOrderInitiated} />
          ),
          renderEditCell: params => <PMWorkbookTableDatePicker {...params} />,
          editable: true,
          filterOperators: allDateOperators
        },
        {
          field: 'year2RedrawOrderNumber',
          headerName: 'Redraw Order Number: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 275,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        },
        {
          field: 'year2RedrawOrderStatus',
          headerName: 'Redraw Order Status: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 265,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          editable: true,
          type: 'singleSelect',
          valueOptions: ({ field, row }) =>
            row === undefined ? columnOptionsRef.current[field] : [...columnOptionsRef.current[field], 'Select'],
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect
        },
        {
          field: 'year2RedrawPhlebotomyConfirmedDate',
          headerName: 'Redraw Phlebotomy Confirmed Date: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 390,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2RedrawPhlebotomyConfirmedDate } }) => (
            <DefaultDateRenderCell value={year2RedrawPhlebotomyConfirmedDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year2RedrawBloodworkCompleted',
          headerName: 'Redraw Bloodwork Completed: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 310,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: value => (value === 'true' ? 'Yes' : null),
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'year2DateOfBloodRedraw',
          headerName: 'Date of Blood Redraw: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 275,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2DateOfBloodRedraw } }) => <DefaultDateRenderCell value={year2DateOfBloodRedraw} />,
          filterOperators: allDateOperators
        },
        {
          field: 'year2RedrawLabRequisitionReceived',
          headerName: 'Redraw Lab Requisition Received: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 380,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { year2RedrawLabRequisitionReceived }) =>
            year2RedrawLabRequisitionReceived?.map(el => el.fileTitle)?.join(', '),
          renderCell: ({ row: { year2RedrawLabRequisitionReceived } }) => (
            <ExpandableLinkList data={year2RedrawLabRequisitionReceived} />
          ),
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'year2RedrawEdcCompletionDate',
          headerName: 'EDC Completion Date: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 260,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { year2RedrawEdcCompletionDate } }) => (
            <DefaultDateRenderCell value={year2RedrawEdcCompletionDate} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'year2RedrawStipend',
          headerName: 'Redraw Stipend: Year 2',
          group: 'Year 2 Repeat Blood Draw',
          width: 240,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'eosCompletionDate',
          headerName: 'EDC Completion Date',
          group: 'EOS Completion',
          width: 210,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { eosCompletionDate }, field }) => (
            <DefaultDateRenderCell value={eosCompletionDate} field={field} />
          ),
          filterOperators: allDateOperators
        },
        {
          field: 'eosReviewer',
          headerName: 'Reviewer',
          group: 'EOS Completion',
          width: 140,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'eosReason',
          headerName: 'EoS Reason',
          group: 'EOS Completion',
          width: 150,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'firstAttemptDueDate',
          headerName: '1st Attempt Due',
          group: 'Medical Records Request',
          width: 180,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { firstAttemptDueDate } }) => <DefaultDateRenderCell value={firstAttemptDueDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'firstAttemptDate',
          headerName: 'Date of 1st Attempt',
          group: 'Medical Records Request',
          width: 195,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { firstAttemptDate } }) => <DefaultDateRenderCell value={firstAttemptDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'methodOfFirstAttempt',
          headerName: 'Method of 1st Attempt',
          group: 'Medical Records Request',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'outcomeOfFirstAttempt',
          headerName: 'Outcome of 1st Attempt',
          group: 'Medical Records Request',
          width: 225,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'firstAttemptCompletedBy',
          headerName: '1st Attempt Completed By',
          group: 'Medical Records Request',
          width: 240,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'secondAttemptDueDate',
          headerName: '2nd Attempt Due',
          group: 'Medical Records Request',
          width: 180,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { secondAttemptDueDate } }) => <DefaultDateRenderCell value={secondAttemptDueDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'secondAttemptDate',
          headerName: 'Date of 2nd Attempt',
          group: 'Medical Records Request',
          width: 200,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { secondAttemptDate } }) => <DefaultDateRenderCell value={secondAttemptDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'methodOfSecondAttempt',
          headerName: 'Method of 2nd Attempt',
          group: 'Medical Records Request',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'outcomeOfSecondAttempt',
          headerName: 'Outcome of 2nd Attempt',
          group: 'Medical Records Request',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'secondAttemptCompletedBy',
          headerName: '2nd Attempt Completed By',
          group: 'Medical Records Request',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'thirdAttemptDueDate',
          headerName: '3rd Attempt Due',
          group: 'Medical Records Request',
          width: 180,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { thirdAttemptDueDate } }) => <DefaultDateRenderCell value={thirdAttemptDueDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'thirdAttemptDate',
          headerName: 'Date of 3rd Attempt',
          group: 'Medical Records Request',
          width: 200,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          type: 'date',
          valueFormatter: defaultDateValueFormatter,
          valueGetter: dateValueGetterWithoutLocalTimezone,
          renderCell: ({ row: { thirdAttemptDate } }) => <DefaultDateRenderCell value={thirdAttemptDate} />,
          filterOperators: allDateOperators
        },
        {
          field: 'methodOfThirdAttempt',
          headerName: 'Method of 3rd Attempt',
          group: 'Medical Records Request',
          width: 220,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'outcomeOfThirdAttempt',
          headerName: 'Outcome of 3rd Attempt',
          group: 'Medical Records Request',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'thirdAttemptCompletedBy',
          headerName: '3rd Attempt Completed By',
          group: 'Medical Records Request',
          width: 240,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'thirtyDayEscalationRequired',
          headerName: '30 Day Escalation Required',
          group: 'Medical Records Request',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'patientMRInvolvementInitiated',
          headerName: 'Patient MR Involvement Initiated',
          group: 'Medical Records Request',
          width: 280,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isEmptyIsNotEmpty
        },
        {
          field: 'recordsAssistanceStipend',
          headerName: 'Records Assistance Stipend',
          group: 'Medical Records Request',
          width: 250,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: DefaultCell,
          filterOperators: isIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'bar83EscalationTasked',
          headerName: '83Bar Escalation Tasked',
          group: 'Medical Records Request',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { bar83EscalationTasked }) => taskFormatter(bar83EscalationTasked)?.join(', '),
          renderCell: ({ row: { bar83EscalationTasked } }) => (
            <ExpandableLinkList data={bar83EscalationTasked} type="link" />
          ),
          filterOperators: taskOperators
        },
        {
          field: 'medicalRecordsTaskNumber',
          headerName: 'Medical Records Task #',
          group: 'Medical Records Request',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { medicalRecordsTaskNumber }) => taskFormatter(medicalRecordsTaskNumber)?.join(', '),
          renderCell: ({ row: { medicalRecordsTaskNumber } }) => (
            <ExpandableLinkList data={medicalRecordsTaskNumber} type="link" />
          ),
          filterOperators: taskOperators
        },
        {
          field: 'currentSubjectStatus',
          headerName: 'Current Subject Status',
          width: 230,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { currentSubjectStatus }) => currentSubjectStatus?.join(', '),
          renderCell: ({ row: { currentSubjectStatus } }) => <DefaultCell value={currentSubjectStatus?.join(', ')} />,
          filterOperators: isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect,
          type: 'singleSelect',
          valueOptions: ({ field }) => columnOptionsRef.current[field]
        },
        {
          field: 'tasks',
          headerName: 'Tasks',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          valueGetter: (value, { tasks }) => taskFormatter(tasks)?.join(', '),
          renderCell: ({ row: { tasks } }) => <ExpandableLinkList data={tasks} type="link" />,
          filterOperators: taskOperators
        },
        {
          field: 'comments',
          headerName: 'Comments',
          width: 300,
          minWidth: PM_WORKBOOK_MIN_COLUMN_WIDTH,
          renderCell: ({ value, field }) => <ExpandableCell value={value} field={field} />,
          editable: true,
          renderEditCell: params => <PMWorkbookEditTextField {...params} />,
          filterOperators: containsIsEmptyIsNotEmpty
        }
      ],
      tableVersion: '1.09',
      tableName: 'PM_WORKBOOK'
    }),
    []
  );
};

export function convertPaymentCard(cardId) {
  const paymentCard = paymentTypeMap?.[cardId];
  return paymentCard ? paymentCard : null;
}

const taskFormatter = tasks =>
  tasks?.map(el => `${el.sequenceNumber}: ${el.topicName} - ${tasksStatusesMap[el.status]?.name}`);
const defaultDateValueFormatter = value => (value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '—');
const DefaultDateRenderCell = ({ value, field }) => (
  <div data-testid={field ? `${field}-cell` : null}>{value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
);
